/**
 * useThreadSSE.ts — Real-time message delivery via Server-Sent Events.
 *
 * Opens a fetch-based SSE connection to GET /requests/{id}/stream so new
 * messages appear instantly without polling. When a new message arrives the
 * React Query cache is updated directly, avoiding a full refetch round-trip.
 *
 * Also handles `watchers_update` events — when one arrives the watcher list
 * is written into the React Query cache under ["watchers", requestId] so the
 * RequestWatchersIndicator component updates live without its own polling.
 *
 * Also handles `request_update` events — sent whenever the request, its
 * Jira tickets, attachments or timeline change — by refetching those queries.
 * While connected, the request page polls only as a slow safety net
 * (see lib/liveUpdates).
 *
 * Reconnects with exponential backoff on network errors. Uses fetch (not
 * EventSource) so the Authorization header can be attached.
 */

import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getAuthToken } from "@/lib/api";
import { hasCookieSession } from "@/lib/auth";
import { threadKeys } from "@/hooks/useThread";
import { requestKeys, ticketKeys } from "@/hooks/useRequests";
import { setLive } from "@/lib/liveUpdates";
import type { Message } from "@/lib/types";

const BASE_URL = import.meta.env.VITE_API_BASE_URL
  ? `${import.meta.env.VITE_API_BASE_URL}/api/v1`
  : "/api/v1";

const MIN_RECONNECT_MS = 1_000;
const MAX_RECONNECT_MS = 30_000;
const MAX_RETRIES = 10;

export function useThreadSSE(requestId: string) {
  const queryClient = useQueryClient();
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!requestId) return;

    let stopped = false;
    let retries = 0;
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;

    // Several changes usually land together (status + timeline + ticket),
    // so refetch once for the burst.
    function refreshRequest() {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        for (const queryKey of [
          requestKeys.detail(requestId),
          requestKeys.timeline(requestId),
          ticketKeys.list(requestId),
        ])
          void queryClient.invalidateQueries({ queryKey });
      }, 300);
    }

    function markLive(connected: boolean) {
      setLive(requestId, connected);
      // Catch up on anything missed while disconnected, and let the
      // queries pick their new poll interval.
      refreshRequest();
    }

    async function connect() {
      if (stopped) return;

      const token = await getAuthToken();
      // Proceed if we have a Bearer token (MSAL) OR a cookie session (email auth)
      if ((!token && !hasCookieSession()) || stopped) return;

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const response = await fetch(
          `${BASE_URL}/requests/${requestId}/stream`,
          {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              Accept: "text/event-stream",
            },
            credentials: "include", // send relay_session cookie when present
            signal: controller.signal,
          },
        );

        if (!response.ok || !response.body) {
          scheduleReconnect();
          return;
        }

        // Successful connection — reset backoff counter.
        retries = 0;
        markLive(true);

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (!stopped) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          // SSE events are delimited by double newlines
          const events = buffer.split("\n\n");
          buffer = events.pop() ?? "";

          for (const event of events) {
            const dataLine = event
              .split("\n")
              .find((l) => l.startsWith("data: "));
            if (!dataLine) continue; // keepalive ping or comment line

            try {
              const parsed = JSON.parse(dataLine.slice(6)) as
                | { type: "watchers_update"; count: number; names: string[] }
                | { type: "request_update" }
                | Message;

              if ("type" in parsed && parsed.type === "request_update") {
                refreshRequest();
              } else if (
                "type" in parsed &&
                parsed.type === "watchers_update"
              ) {
                // Keep watcher list fresh in the cache so RequestWatchersIndicator
                // updates without its own polling.
                queryClient.setQueryData(["watchers", requestId], {
                  count: parsed.count,
                  names: parsed.names,
                });
              } else {
                const msg = parsed as Message;
                queryClient.setQueryData<Message[]>(
                  threadKeys.all(requestId),
                  (old) => {
                    if (!old) return [msg];
                    // Avoid duplicates (post_message already added it optimistically
                    // in some flows)
                    const exists = old.some((m) => m.id === msg.id);
                    return exists ? old : [...old, msg];
                  },
                );
              }
            } catch {
              // Malformed JSON — ignore and continue
            }
          }
        }
        // The server closed the stream (deploy, replica restart) — reconnect
        // rather than silently going without live updates.
        scheduleReconnect();
      } catch (err: unknown) {
        if (err instanceof Error && err.name === "AbortError") return;
        scheduleReconnect();
      }
    }

    function scheduleReconnect() {
      if (stopped) return;
      markLive(false);
      if (retries >= MAX_RETRIES) return;
      const delay = Math.min(MIN_RECONNECT_MS * 2 ** retries, MAX_RECONNECT_MS);
      retries++;
      setTimeout(connect, delay);
    }

    connect();

    return () => {
      stopped = true;
      clearTimeout(refreshTimer);
      setLive(requestId, false);
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, [requestId, queryClient]);
}
